import React, { useEffect, useState } from 'react';
import { GoogleMap, LoadScript, InfoWindow, Marker } from '@react-google-maps/api';
import MapStyle from "./mapStyle.json";

// Define libraries as a static array outside the component
const libraries = ['geometry'];

const containerStyle = {
  width: '100%',
  height: '100%'
};

const Map = ({ data, handleActiveMarker, active, setActive, range, location, setLocation, onMapsLoaded, setSelectedHealerId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isMounted, setIsMounted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [shouldRenderMap, setShouldRenderMap] = useState(false);

  // Add proper null checks for data
  const safeData = data || {};
  const safeMarkers = safeData.markers || [];
  const safeHealers = safeData.healers || [];

  useEffect(() => {
    setIsMounted(true);
    setShouldRenderMap(false);
    setHasError(false);

    // Add global error handler for Google Maps API
    const handleGoogleMapsError = (error) => {
      console.error('Google Maps API Error:', error);
      setHasError(true);
    };

    window.addEventListener('error', handleGoogleMapsError);

    return () => {
      setIsMounted(false);
      setShouldRenderMap(false);
      setHasError(false);
      // Clean up any Google Maps instances
      if (window.google && window.google.maps) {
        try {
          // Clear any active info windows
          const infoWindows = document.querySelectorAll('.gm-style-iw');
          infoWindows.forEach(window => {
            if (window && window.close) {
              window.close();
            }
          });
        } catch (error) {
          console.log('Map cleanup error:', error);
        }
      }
      window.removeEventListener('error', handleGoogleMapsError);
    };
  }, []);

  useEffect(() => {
    if (isMounted) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setLocation({ view: { lat: pos.coords.latitude, lng: pos.coords.longitude } }),
        () => setLocation({ view: { lat: 48.407326, lng: -123.329773 } })
      );
    }
  }, [setLocation, isMounted]);

  console.log("Markers passed to Map:", safeMarkers);

  useEffect(() => {
    if (isMounted) {
      // Simulate loading time for better UX
      const timer = setTimeout(() => {
        setIsLoading(false);
        // Add additional delay before rendering map
        setTimeout(() => {
          setShouldRenderMap(true);
        }, 500);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isMounted]);

  // Don't render if not mounted, has error, or shouldn't render map yet
  if (!isMounted || hasError || !shouldRenderMap) {
    return (
      <div className="mapFrame">
        <div className="map-inner-frame">
          <div className="loaderContainer">
            <div className="loader"></div>
          </div>
        </div>
      </div>
    );
  }

  try {
    return (
      <div className="mapFrame">
        <div className="map-inner-frame">
          <div className="map-header-frame"></div>

          {/* Corner decorations */}
          <div className="map-corner-decoration top-left"></div>
          <div className="map-corner-decoration top-right"></div>
          <div className="map-corner-decoration bottom-left"></div>
          <div className="map-corner-decoration bottom-right"></div>

          {/* Map stats overlay */}
          <div className="map-stats-overlay">
            <div className="map-stats-item">
              <svg className="map-stats-icon" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{safeMarkers.length} healers on map</span>
            </div>
            <div className="map-stats-item">
              <svg className="map-stats-icon" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" />
              </svg>
              <span>Real-time data</span>
            </div>
            {safeHealers.length > safeMarkers.length && (
              <div className="map-stats-item" style={{ fontSize: '0.75rem', color: '#888', fontStyle: 'italic' }}>
                <svg className="map-stats-icon" fill="currentColor" viewBox="0 0 20 20" style={{ width: '12px', height: '12px' }}>
                  <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" />
                </svg>
                <span>{safeHealers.length - safeMarkers.length} more in list view</span>
              </div>
            )}
          </div>

          {/* Loading overlay */}
          {isLoading && (
            <div className="map-loading-overlay">
              <div className="map-loading-spinner"></div>
            </div>
          )}

          {shouldRenderMap && (
            <LoadScript
              googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyD4YzQMWoVijPevGTur61ekzEvgzlrkAyI'}
              libraries={libraries} // Use static array
              onLoad={() => {
                if (onMapsLoaded) {
                  onMapsLoaded();
                }
              }}
              onError={() => {
                console.error('Google Maps failed to load');
                setHasError(true);
              }}
            >
              <GoogleMap
                onClick={() => setActive(null)}
                options={{
                  streetViewControl: false,
                  mapTypeControl: false,
                  styles: MapStyle
                }}
                mapContainerStyle={containerStyle}
                center={location.view}
                zoom={12}
              >
                {safeMarkers.map(({ id, healer, position, description, services, rating, reviewsCount }) => (
                  <Marker
                    key={id}
                    position={position}
                    onClick={() => handleActiveMarker(id)}
                    icon={{
                      url: 'data:image/svg+xml;utf-8,' + encodeURIComponent(`
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                      <feDropShadow dx="0" dy="4" stdDeviation="4" flood-color="rgba(38, 168, 153, 0.4)"/>
                    </filter>
                    <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                      <feMerge> 
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                    <linearGradient id="markerGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style="stop-color:#26a899;stop-opacity:1" />
                      <stop offset="50%" style="stop-color:#1abc9c;stop-opacity:1" />
                      <stop offset="100%" style="stop-color:#16a085;stop-opacity:1" />
                    </linearGradient>
                    <radialGradient id="markerGlow" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" style="stop-color:#26a899;stop-opacity:0.8" />
                      <stop offset="100%" style="stop-color:#26a899;stop-opacity:0" />
                    </radialGradient>
                  </defs>
                  <!-- Glow effect -->
                  <circle cx="24" cy="24" r="22" fill="url(#markerGlow)" opacity="0.6"/>
                  <!-- Main marker -->
                  <circle cx="24" cy="24" r="18" fill="url(#markerGradient)" stroke="#fff" stroke-width="3" filter="url(#shadow)"/>
                  <!-- Inner highlight -->
                  <circle cx="20" cy="20" r="8" fill="rgba(255,255,255,0.3)"/>
                  <!-- Person icon -->
                  <circle cx="24" cy="18" r="6" fill="#fff"/>
                  <ellipse cx="24" cy="30" rx="10" ry="6" fill="#fff"/>
                  <!-- Center dot -->
                  <circle cx="24" cy="24" r="3" fill="rgba(255,255,255,0.8)"/>
                </svg>
              `),
                      scaledSize: { width: 48, height: 48 }
                    }}
                  >
                    {active === id && (
                      <InfoWindow>
                        <div style={{
                          width: '260px',
                          background: '#fff',
                          borderRadius: '12px',
                          boxShadow: '0 8px 32px rgba(156, 112, 243, 0.2)',
                          border: '1px solid rgba(156, 112, 243, 0.1)',
                          overflow: 'hidden'
                        }}>
                          {/* Content */}
                          <div style={{
                            padding: '1rem',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.75rem'
                          }}>
                            {/* Healer Name with Close Button */}
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '0.25rem'
                            }}>
                              <div style={{
                                color: '#26a899',
                                fontWeight: '700',
                                fontSize: '1rem'
                              }}>
                                {healer}
                              </div>
                              <button
                                onClick={() => setActive(null)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#26a899',
                                  fontSize: '1.2rem',
                                  cursor: 'pointer',
                                  padding: '0',
                                  width: '20px',
                                  height: '20px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 'bold'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.opacity = '0.7';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.opacity = '1';
                                }}
                              >
                                ×
                              </button>
                            </div>
                            {/* Services */}
                            <div style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '0.5rem',
                              justifyContent: 'center',
                              marginBottom: '0.25rem'
                            }}>
                              {services ? (
                                services.split(',').slice(0, 3).map((service, index) => (
                                  <span key={index} style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '0.25rem 0.5rem',
                                    background: 'linear-gradient(135deg, rgba(156, 112, 243, 0.08) 0%, rgba(38, 168, 153, 0.08) 100%)',
                                    border: '1px solid rgba(156, 112, 243, 0.15)',
                                    borderRadius: '12px',
                                    fontSize: '0.7rem',
                                    fontWeight: '500',
                                    color: '#666',
                                    transition: 'all 0.2s ease',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {service.trim()}
                                  </span>
                                ))
                              ) : (
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '0.25rem 0.5rem',
                                  background: 'linear-gradient(135deg, rgba(156, 112, 243, 0.08) 0%, rgba(38, 168, 153, 0.08) 100%)',
                                  border: '1px solid rgba(156, 112, 243, 0.15)',
                                  borderRadius: '12px',
                                  fontSize: '0.7rem',
                                  fontWeight: '500',
                                  color: '#666'
                                }}>
                                  No services listed
                                </span>
                              )}
                              {services && services.split(',').length > 3 && (
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '0.25rem 0.5rem',
                                  background: 'linear-gradient(135deg, rgba(156, 112, 243, 0.08) 0%, rgba(38, 168, 153, 0.08) 100%)',
                                  border: '1px solid rgba(156, 112, 243, 0.15)',
                                  borderRadius: '12px',
                                  fontSize: '0.7rem',
                                  fontWeight: '500',
                                  color: '#666'
                                }}>
                                  +{services.split(',').length - 3} more
                                </span>
                              )}
                            </div>

                            {/* Rating */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.25rem'
                            }}>
                              {[...Array(5)].map((_, i) => (
                                <span key={i} style={{
                                  color: i < (rating || 0) ? '#ffd700' : '#e0e0e0',
                                  fontSize: '0.75rem'
                                }}>
                                  ★
                                </span>
                              ))}
                              <span style={{
                                color: '#666',
                                fontSize: '0.7rem',
                                marginLeft: '0.5rem'
                              }}>
                                {rating > 0 ? `${rating} (${reviewsCount} reviews)` : 'No reviews yet'}
                              </span>
                            </div>

                            {/* Description */}
                            <div style={{
                              color: '#666',
                              fontSize: '0.75rem',
                              lineHeight: '1.3',
                              textAlign: 'center',
                              maxHeight: '40px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}>
                              {description || 'No description available'}
                            </div>

                            {/* Details Button */}
                            <button
                              onClick={() => {
                                setSelectedHealerId(id);
                                setActive(null);
                              }}
                              style={{
                                background: 'linear-gradient(135deg, #26a899 0%, #1abc9c 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '0.6rem',
                                fontSize: '0.8rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                width: '100%',
                                boxShadow: '0 3px 8px rgba(38, 168, 153, 0.3)',
                                marginTop: '0.25rem'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.transform = 'translateY(-1px)';
                                e.target.style.boxShadow = '0 4px 12px rgba(38, 168, 153, 0.4)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 3px 8px rgba(38, 168, 153, 0.3)';
                              }}
                            >
                              View Details & Book
                            </button>
                          </div>
                        </div>
                      </InfoWindow>
                    )}
                  </Marker>
                ))}
              </GoogleMap>
            </LoadScript>
          )}
        </div>
      </div>
    );
  } catch (error) {
    console.error('Error rendering map:', error);
    setHasError(true);
    return (
      <div className="mapFrame">
        <div className="map-inner-frame">
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#666',
            fontSize: '1rem'
          }}>
            Map loading error. Please try refreshing the page.
          </div>
        </div>
      </div>
    );
  }
};

export default Map;

